import { listAppRecommendations } from '@wix/ambassador-funnel-app-recommendation-v1-app-recommendation/http'
import { AppRecommendation } from '@wix/ambassador-funnel-app-recommendation-v1-app-recommendation/types'
import { WIX_BOOKINGS, WIX_SERVICES } from '@wix/app-definition-ids'
import { Box, Button, Checkbox, Image, Text } from '@wix/design-system'
import {
  ChoiceQuestionContentComponent,
  GroupContentComponent,
  useContent,
  useExperiments,
  useMetadata,
  useNext,
} from '@wix/funnel-builder-lizard'
import { tryCatch } from '@wix/funnel-utils'
import classNames from 'classnames'
import React, { FC, ReactNode, useEffect, useState } from 'react'

import { ambassadorHttpClient } from '../../../api/httpClient'
import { usePageLogger } from '../../../bi/mfi'
import { resolveCategoryFromBusinessTerm } from '../../../category'
import { useStorageContext } from '../../../context/StorageContext/StorageContext'
import { DesignSystemTokensLoader } from '../../reusable/DesignSystemTokensLoader'
import { WixMobileBackButton } from '../../reusable/WixMobileBackButton/WixMobileBackButton'
import { WixMobileContentWrapper } from '../../reusable/WixMobileContentWrapper/WixMobileContentWrapper'
import { WixMobileDescription } from '../../reusable/WixMobileDescription/WixMobileDescription'
import { WixMobileFooter } from '../../reusable/WixMobileFooter/WixMobileFooter'
import { WixMobileHeader } from '../../reusable/WixMobileHeader/WixMobileHeader'
import { WixMobileHeading } from '../../reusable/WixMobileHeading/WixMobileHeading'
import { WixMobilePage } from '../../reusable/WixMobilePage/WixMobilePage'
import { WixMobileSkipButton } from '../../reusable/WixMobileSkipButton/WixMobileSkipButton'
import { WixMobileStepProgressBar } from '../../reusable/WixMobileStepProgressBar/WixMobileStepProgressBar'
import {
  type App,
  AppLevel,
  FALLBACK_APPS,
  getBIAnswer,
  getBIAppInstallation,
  mergeAppsData,
  segmentToAppIds,
  segmentToAppIdsV2,
} from './common'
import { wixMobileAppsDataHooks as DataHooks } from './WixMobileApps.dataHooks'
import s from './WixMobileApps.scss'

export type Content = {
  builtInLabel: string
  back: string
  next: string
  skip: string
  skin?: 'default' | 'harmony'
  stepProgressBar: GroupContentComponent<{ visible: boolean; total: number; current: number }>
  question: ChoiceQuestionContentComponent<{ title: string; subtitle: string }, App>
}

type ItemProps = {
  app: App
  isActive: boolean
  isHarmony: boolean
  onClick: (appDefId: string) => void
}

const Item: FC<ItemProps> = ({ app, isActive, isHarmony, onClick }) => (
  <li
    className={classNames(s.WixMobileApps__item, {
      [s['WixMobileApps__item--active']]: isActive,
      [s.harmonyItem]: isHarmony,
    })}
    onClick={() => onClick(app.appDefId)}
    data-hook={DataHooks.appItem({ key: app.optionKey })}
  >
    <Image className={s.WixMobileApps__itemImage} src={app.icon} transparent />
    <div className={s.WixMobileApps__itemContent}>
      <Box className={s.WixMobileApps__itemTitle}>
        <Text
          weight={isHarmony ? 'bold' : 'normal'}
          className={isHarmony ? classNames(s.harmonyItemTitle, { [s.harmonyItemTitleActive]: isActive }) : undefined}
        >
          {app.name}
        </Text>
      </Box>
      <Text size="small" className={isHarmony ? s.harmonyItemDescription : undefined}>
        {app.description}
      </Text>
    </div>
    <Checkbox
      checked={isActive}
      dataHook={DataHooks.appItemCheckbox({ key: app.optionKey })}
      className={isHarmony ? s.harmonyItemCheck : undefined}
    />
  </li>
)

export const WixMobileApps: FC = () => {
  const [values, setValues] = useState<string[]>([])
  const [mergedApps, setMergedApps] = useState<App[]>([])
  const [{ biSlug: flowBiSlug }] = useMetadata()
  const { content } = useContent<Content>()
  const { category, save } = useStorageContext()
  const pageLogger = usePageLogger()
  const next = useNext()
  const experiments = useExperiments()
  const isHarmony = content.skin === 'harmony'
  const { industryId, structureId, businessSegmentId } = resolveCategoryFromBusinessTerm(
    category?.businessTerm,
    category?.businessSegmentId,
  )

  const onToggle = (value: string) => {
    pageLogger.clickAnswer({
      questionKey: content.question.questionKey,
      buttonName: content.question.options.find(o => o.optionKey === value)?.biSlug || value,
    })

    if (values.includes(value)) {
      setValues(values.filter(v => v !== value))
    } else {
      setValues([...values, value])
    }
  }

  const appsSelectedBI = () => {
    const { appId, builtInAppId } = getBIAppInstallation(mergedApps, values)

    pageLogger.appsSelected({ app_id: appId, built_in_app_id: builtInAppId })
  }

  const saveApps = () =>
    save({
      appDefIds: values
        .map(optionKey => mergedApps.find(a => a.optionKey === optionKey)?.appDefId)
        .filter(a => typeof a === 'string') as string[],
    })

  const onSkipButtonClick = () => {
    pageLogger.skip({
      payload: getBIAnswer(content.question.options, mergedApps, values),
    })

    appsSelectedBI()
    saveApps()
    next({ [content.question.questionKey]: { skipped: true } })
  }

  const onNextButtonClick = () => {
    pageLogger.answer({
      answer: getBIAnswer(content.question.options, mergedApps, values),
    })

    appsSelectedBI()
    saveApps()
    next({ [content.question.questionKey]: { values } })
  }

  const onBackButtonClick = () => {
    pageLogger.back({
      payload: getBIAnswer(content.question.options, mergedApps, values),
    })

    saveApps()
    window.history.back()
  }

  useEffect(() => {
    const doLoad = async () => {
      const request = listAppRecommendations({
        origin: flowBiSlug,
        structureId,
        industryId,
      })
      const [, response] = await tryCatch(() => ambassadorHttpClient.request(request))
      const recommendedApps = (response?.data.apps || FALLBACK_APPS) as (AppRecommendation & { level?: AppLevel })[]
      const merged = mergeAppsData(recommendedApps, content.question.options)

      const mapping = experiments['specs.funnel.SectorToAppV2'] === 'true' ? segmentToAppIdsV2 : segmentToAppIds
      let sectorAppIds = mapping[businessSegmentId] || []
      if (experiments['specs.funnel.ServicesApp'] === 'true' && businessSegmentId === 'business-professionals') {
        sectorAppIds = sectorAppIds.map(id => (id === WIX_BOOKINGS ? WIX_SERVICES : id))
      }
      const prechecked = merged.filter(app => sectorAppIds.includes(app.appDefId)).map(app => app.optionKey)
      const sorted = [
        ...merged.filter(app => sectorAppIds.includes(app.appDefId)),
        ...merged.filter(app => !sectorAppIds.includes(app.appDefId)),
      ]

      pageLogger.screenView({
        initialData: getBIAnswer(content.question.options, sorted, prechecked),
      })

      setMergedApps(sorted)
      setValues(prechecked)
    }
    void doLoad()
  }, [])

  const body = (
    <WixMobilePage>
      <div className={classNames({ [s.harmonyRoot]: isHarmony })}>
        <WixMobileHeader
          skin={isHarmony ? 'harmony' : 'standard'}
          leftContent={
            (content.back && (
              <WixMobileBackButton dataHook={DataHooks.back()} onBack={onBackButtonClick} label={content.back} />
            )) ||
            undefined
          }
          rightContent={
            (content.skip && (
              <WixMobileSkipButton dataHook={DataHooks.skip()} onSkip={onSkipButtonClick} label={content.skip} />
            )) ||
            undefined
          }
        />
        <WixMobileContentWrapper>
          {content.stepProgressBar.content.visible && (
            <WixMobileStepProgressBar
              current={content.stepProgressBar.content.current}
              total={content.stepProgressBar.content.total}
              skin={isHarmony ? 'harmony' : 'default'}
              dataHook={DataHooks.stepProgressBar()}
            />
          )}
          <WixMobileHeading dataHook={DataHooks.title()} skin={isHarmony ? 'harmony' : 'default'}>
            {content.question.content.title}
          </WixMobileHeading>
          <WixMobileDescription dataHook={DataHooks.subtitle()} skin={isHarmony ? 'harmony' : 'default'}>
            {content.question.content.subtitle}
          </WixMobileDescription>
          <ul className={s.WixMobileApps}>
            {mergedApps.map(app => (
              <Item
                key={app.appDefId}
                app={app}
                isActive={values.includes(app.optionKey)}
                isHarmony={isHarmony}
                onClick={() => onToggle(app.optionKey)}
              />
            ))}
          </ul>
        </WixMobileContentWrapper>
        {content.next && (
          <WixMobileFooter>
            <Button
              size="large"
              fullWidth
              disabled={!values.length}
              onClick={onNextButtonClick}
              dataHook={DataHooks.next()}
              className={isHarmony ? s.harmonyNextButton : undefined}
            >
              {content.next}
            </Button>
          </WixMobileFooter>
        )}
      </div>
    </WixMobilePage>
  )

  if (isHarmony) {
    return <DesignSystemTokensLoader type="odeditor">{body}</DesignSystemTokensLoader>
  }
  return body
}
